import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { LetDirective } from '@ngrx/component';
import { isNil } from 'lodash';
import moment from 'moment-timezone';
import { BehaviorSubject, combineLatest, defer, filter, firstValueFrom, startWith, switchMap, tap } from 'rxjs';

import { getFormSubmissionReportInfo } from '@pwp-common';

import { makePTableCols } from '../../../../common/p-table/make-p-table-cols';
import { OrgDataService } from '../../../../services/orgs/org-data/org-data.service';
import { UserDataService } from '../../../../services/user/user-data/user-data.service';
import { DateRangeSelectOutput } from '../../../core/date-range-select/common/date-range-select-output';
import { DateRangeSelectModule } from '../../../core/date-range-select/date-range-select.module';
import { FormSubmissionService } from '../../../form/services/form-submission/form-submission.service';
import { FormVersionService } from '../../../form/services/form-version.service';
import { FormService } from '../../../form/services/form.service';
import { DataTableModule } from '../../../generic/data-table/data-table.module';
import { StatusBadgeColor } from '../../../shared/status-badge/status-badge-color';
import { StatusBadgeComponent } from '../../../shared/status-badge/status-badge.component';

import { FormSubmissionChartComponent } from '../form-submission-chart/form-submission-chart.component';
import { createChartTableRows } from './helpers/create-chart-table-rows/create-chart-table-rows';
import { ChartTableRow } from './helpers/create-chart-table-rows/interfaces';

@Component({
  selector: 'app-form-submission-chart-table',
  standalone: true,
  imports: [
    CommonModule,
    DataTableModule,
    DateRangeSelectModule,
    FormSubmissionChartComponent,
    LetDirective,
    ReactiveFormsModule,
    StatusBadgeComponent,
  ],
  templateUrl: './form-submission-chart-table.component.html',
  styleUrls: ['./form-submission-chart-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormSubmissionChartTableComponent {
  ///////////////////////////////////////////////////////////////////////
  // Variables
  ///////////////////////////////////////////////////////////////////////

  private readonly formSubmissionService = inject(FormSubmissionService);

  private readonly formVersionService = inject(FormVersionService);

  private readonly formService = inject(FormService);

  private readonly orgDataService = inject(OrgDataService);

  private readonly userDataService = inject(UserDataService);

  private readonly allUserDataMap$ = this.userDataService.getDocs();

  private readonly orgData$ = this.orgDataService.getOrgData();

  private readonly selectedDateRange$ = defer(() =>
    this.dateRange.valueChanges.pipe(
      startWith(this.dateRange.value),
      filter((range) => !isNil(range)),
    ),
  );

  ///////////////////////////////////////////////////////////////////////
  // Public
  ///////////////////////////////////////////////////////////////////////

  public readonly statusBadgeColor = StatusBadgeColor.green;

  public readonly dateRange = new FormControl<DateRangeSelectOutput>({
    start: moment().subtract(1, 'day').startOf('day').toDate(),
    end: moment().endOf('day').toDate(),
  });

  public readonly cols = makePTableCols({
    columns: ['name', 'isLatestVersion', 'createdBy', 'createTime', 'submissionCount', 'formId', 'formVersionId'],
    translationScope: 'form-submission-chart-table',
  });

  public readonly selectedCols = makePTableCols({
    columns: ['name', 'isLatestVersion', 'createdBy', 'createTime', 'submissionCount'],
    translationScope: 'form-submission-chart-table',
  });

  public readonly loading$ = new BehaviorSubject(false);

  public readonly tableRows$ = this.selectedDateRange$.pipe(
    tap(() => this.loading$.next(true)),
    switchMap((range) => this.getRows(range)),
    tap(() => this.loading$.next(false)),
  );

  ///////////////////////////////////////////////////////////////////////
  // Private methods
  ///////////////////////////////////////////////////////////////////////

  private async getRows(range: DateRangeSelectOutput): Promise<ChartTableRow[]> {
    const [formSubmissions, allUserDataMap, orgData] = await firstValueFrom(
      combineLatest([this.formSubmissionService.getFormSubmissionsIn(range), this.allUserDataMap$, this.orgData$]),
    );

    const { formVersionIds } = getFormSubmissionReportInfo(formSubmissions);

    const formVersions = await firstValueFrom(this.formVersionService.getDocsWithIds(Array.from(formVersionIds)));

    const forms = await firstValueFrom(
      this.formService.getDocsWithIds(Array.from(formVersions.values()).map((fv) => fv.getFormId())),
    );

    return createChartTableRows({
      formSubmissions,
      formVersions,
      forms,
      allUserDataMap,
      timezone: orgData.getTimezone(),
    });
  }
}
